import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps a standalone free-delivery progress section in step with the cart.
 *
 * Why this exists: Horizon's cart re-render (assets/component-cart-items.js) builds its set of
 * sections to refresh from the `cart-items-component` elements present on the page. A progress
 * bar placed anywhere else — a landing page, a product page, above the footer — is not in that
 * set, so after an add-to-cart or a quantity change it would keep showing the figure it was
 * rendered with. That is worse than showing nothing: "Add £12.50 more" is a claim, and a stale
 * one is a false claim.
 *
 * Rather than recompute the threshold maths in JavaScript, this re-requests the section and lets
 * Liquid stay the single source of truth. There is exactly one implementation of the money
 * rounding, the qualified/remaining wording and the blank-threshold guard, and it lives in
 * snippets/aala-shipping-progress.liquid.
 *
 * Deliberately extends HTMLElement rather than Horizon's Component base class: this element has
 * no refs and no internal state, so the ref machinery would add failure modes for no benefit.
 *
 * Inside the cart drawer this element is not used — there, Horizon's own keyed morph already
 * updates the snippet, which is why the snippet hides itself with the `hidden` attribute instead
 * of being removed.
 */
class AalaShippingProgress extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    // A fresh controller per connection: morphing can disconnect and reconnect this element, and
    // an aborted signal can never be reused, so reusing one would silently stop the listener.
    this.#controller = new AbortController();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, {
      signal: this.#controller.signal,
    });

    // A discount code applied or removed on the cart page (assets/cart-discount.js) changes
    // cart.total_price, which is the free-delivery basis, and can replace the automatic discount.
    // It dispatches the same shape of event with a deferred promise, so the one handler serves both.
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * The id Shopify's Section Rendering API expects, e.g. `template--123__aala_shipping_progress_ABC`.
   * @returns {string | null}
   */
  get #sectionId() {
    if (this.dataset.sectionId) return this.dataset.sectionId;

    const wrapper = this.closest('.shopify-section');

    return wrapper?.id.replace(/^shopify-section-/, '') ?? null;
  }

  /**
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onCartUpdate = (event) => {
    const sectionId = this.#sectionId;

    if (!sectionId) return;

    // Wait for the cart mutation to settle before re-requesting, or the section renders against
    // the pre-update cart and we simply paint the stale figure again.
    Promise.resolve(event.promise)
      .then(() => sectionRenderer.renderSection(sectionId, { cache: false }))
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        // Leave the previous render in place. It may be stale, but tearing the component out
        // mid-shop is worse than a figure that corrects itself on the next navigation.
        console.warn('[aala-shipping-progress] Could not refresh after a cart update:', error);
      });
  };
}

if (!customElements.get('aala-shipping-progress')) {
  customElements.define('aala-shipping-progress', AalaShippingProgress);
}
